import React, { useMemo, useRef, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BeatLoader } from 'react-spinners';
import RestService from '../Services/RestService';
import OKomBreadcrumb from '../Components/OsloKommuneUI/OKomBreadcrumb';
import OKomLoading from '../Components/OsloKommuneUI/OKomLoading';
import OKomModal from '../Components/OsloKommuneUI/OKomModal';
import OKomRadioGroup from '../Components/OsloKommuneUI/OKomRadioGroup';
import { useUserContext } from '../Components/StateContext/UserContext';

type RegType = '' | 'exam' | 'athletics';

interface Recipient {
  Email: string;
  Names: string;
  RegistrationCount: number;
}

// Tokens the backend (MassEmailContentBuilder) expands per recipient. Keep in sync with it.
const VARIABLES: { token: string; label: string; help: string }[] = [
  { token: '{{registrering}}', label: 'Registrering (norsk)', help: 'Tabell med norske kolonner (Navn / Kjønn / Fødselsdato / Lag / Avdeling / Registrerte konkurranser) – én rad per barn.' },
  { token: '{{registrering_tamil}}', label: 'Registrering (tamil)', help: 'Samme tabell med tamilske kolonner og verdier (øvelsesnavn, kjønn, lag på tamil) – én rad per barn.' },
  { token: '{{registreringstabell}}', label: 'Registreringstabell', help: 'Tabell over barna som er påmeldt under denne mottakeren (samme som bekreftelses-e-posten).' },
  { token: '{{foreldretabell}}', label: 'Foreldretabell', help: 'Tabell med foreldrenes e-post og mobilnummer for mottakerens påmelding(er).' },
  { token: '{{praktisk_info}}', label: 'Praktisk info', help: 'Dato- og adresseblokk for valgt påmeldingstype (samme som bekreftelses-e-posten).' },
];

const SPORTSREGLER_URL = 'https://portal.annaipoopathi.no/assets/APTAKsportsregler2026_v5.pdf';
const KJOREPLAN_URL = 'https://portal.annaipoopathi.no/assets/APTAKkjoreplan2026.pdf';

// Default bilingual (norsk + tamil) body for the athletics pre-event mail. The {{registrering*}}
// tokens are filled per recipient when sending.
const ATHLETICS_TEMPLATE = `<p>Hei,</p>
<p>Vi sender herved registrerte opplysninger for deres barn i forbindelse med idrettsarrangementet.</p>
<p>Følgende informasjon er registrert:</p>
{{registrering}}
<p>Regler for arrangementet og kjøreplan er vedlagt.</p>
<ul>
  <li>Regler for arrangementet: <a href="${SPORTSREGLER_URL}">${SPORTSREGLER_URL}</a></li>
  <li>Kjøreplan: <a href="${KJOREPLAN_URL}">${KJOREPLAN_URL}</a></li>
</ul>
<p>Med vennlig hilsen<br>Administrasjonen</p>
<hr>
<p>வணக்கம்,</p>
<p>தங்களின் பிள்ளைகளின் விளையாட்டு போட்டி பதிவு விவரங்களை இத்துடன் இணைத்துள்ளோம்.</p>
<p>பதிவு செய்யப்பட்டுள்ள தகவல்கள்:</p>
{{registrering_tamil}}
<p>விளையாட்டு நிகழ்வுக்கான விதிமுறைகள் மற்றும் நிகழ்ச்சி நிரல் இணைக்கப்பட்டுள்ளது.</p>
<ul>
  <li>விளையாட்டு நிகழ்வு விதிமுறைகள்: <a href="${SPORTSREGLER_URL}">${SPORTSREGLER_URL}</a></li>
  <li>விளையாட்டு நிகழ்வு: <a href="${KJOREPLAN_URL}">${KJOREPLAN_URL}</a></li>
</ul>
<p>நன்றி.<br>நிர்வாகம்</p>`;

// Simpler analogous default for the exam type (no sports rules / kjøreplan).
const EXAM_TEMPLATE = `<p>Hei,</p>
<p>Vi sender herved registrerte opplysninger for deres barn i forbindelse med eksamen.</p>
<p>Følgende informasjon er registrert:</p>
{{registrering}}
{{praktisk_info}}
<p>Med vennlig hilsen<br>Administrasjonen</p>
<hr>
<p>வணக்கம்,</p>
<p>தங்களின் பிள்ளைகளின் தேர்வுப் பதிவு விவரங்களை இத்துடன் இணைத்துள்ளோம்:</p>
{{registrering_tamil}}
<p>நன்றி.<br>நிர்வாகம்</p>`;

const templateFor = (t: RegType): string =>
  t === 'athletics' ? ATHLETICS_TEMPLATE : t === 'exam' ? EXAM_TEMPLATE : '';

const MasseutsendelserView: React.FC<RouteComponentProps> = () => {
  const { userRoles } = useUserContext();
  const isAdmin = userRoles.includes('admin');

  const [type, setType] = useState<RegType>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewRecipient, setPreviewRecipient] = useState<string | null>(null);

  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const loadRecipients = async (t: RegType) => {
    if (t !== 'exam' && t !== 'athletics') return;
    setLoadingRecipients(true);
    try {
      const data = await RestService.get<Recipient[]>(`/api/massemail/Recipients?type=${t}`);
      setRecipients(data ?? []);
      setSelected(new Set());   // none selected by default
    } catch (e) {
      toast.error(`Kunne ikke hente mottakere: ${e}`);
      setRecipients([]);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const onTypeChange = (value: string) => {
    const t = value as RegType;
    setType(t);
    setSearch('');
    // Pre-fill the default bilingual template the first time (don't clobber typed text).
    if (!body.trim()) setBody(templateFor(t));
    if (!subject.trim()) setSubject(t === 'athletics' ? 'Idrettsarrangement – registrerte opplysninger' : 'Eksamen – registrerte opplysninger');
    loadRecipients(t);
  };

  const insertTemplate = () => {
    if (!type) return;
    if (body.trim() && !window.confirm('Erstatte gjeldende e-posttekst med standardmalen?')) return;
    setBody(templateFor(type));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return recipients;
    return recipients.filter(r => `${r.Email} ${r.Names}`.toLowerCase().includes(q));
  }, [recipients, search]);

  const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.Email));

  const toggleOne = (email: string, checked: boolean) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) next.add(email); else next.delete(email);
      return next;
    });

  const toggleAllFiltered = (checked: boolean) =>
    setSelected(prev => {
      const next = new Set(prev);
      filtered.forEach(r => { if (checked) next.add(r.Email); else next.delete(r.Email); });
      return next;
    });

  // Insert a variable token at the textarea caret (or append if not focused).
  const insertVariable = (token: string) => {
    const el = bodyRef.current;
    if (!el) { setBody(b => b + token); return; }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    // Restore caret just after the inserted token.
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + token.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const openPreview = async () => {
    if (!type || !body.trim()) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewHtml('');
    setPreviewRecipient(null);
    try {
      const res = await RestService.post<
        { Type: string; Subject: string; BodyHtml: string; Recipients: string[] },
        { Html: string; Recipient: string | null }
      >('/api/massemail/Preview', {
        Type: type,
        Subject: subject,
        BodyHtml: body,
        Recipients: Array.from(selected),
      });
      setPreviewHtml(res.Html);
      setPreviewRecipient(res.Recipient);
    } catch (e) {
      toast.error(`Kunne ikke lage forhåndsvisning: ${e}`);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const canPreview = !!type && body.trim().length > 0;
  const canSend = !!type && subject.trim().length > 0 && body.trim().length > 0 && selected.size > 0;

  const send = async () => {
    setSending(true);
    try {
      const res = await RestService.post<
        { Type: string; Subject: string; BodyHtml: string; Recipients: string[] },
        { Queued: boolean; Count: number }
      >('/api/massemail/Send', {
        Type: type,
        Subject: subject.trim(),
        BodyHtml: body,
        Recipients: Array.from(selected),
      });
      toast.success(`${res.Count} e-post${res.Count === 1 ? '' : 'er'} lagt i kø for utsending.`);
      setConfirmOpen(false);
    } catch (e) {
      toast.error(`Kunne ikke sende: ${e}`);
    } finally {
      setSending(false);
    }
  };

  if (!isAdmin) {
    return (
      <>
        <OKomBreadcrumb path='Epost, Masseutsendelser' />
        <div className="ods-grid ods-grid--center ods-margin-top-25">
          <div className="ods-grid__column--8">
            <div className="ods-status-card ods-color-bg-red-light">
              <div className="ods-status-card__content">
                <span className="ods-text--size-kilo">Ingen tilgang. Denne siden er kun for administratorer.</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <OKomBreadcrumb path='Epost, Masseutsendelser' />
      <div className="ods-margin-top-5" style={{ padding: '0 1.5rem', maxWidth: '60rem', margin: '0 auto' }}>
        <h2 style={{ marginTop: 0 }}>Masseutsendelser</h2>

        <div className="ods-margin-top-3">
          <OKomRadioGroup
            legend='Påmeldingstype'
            name='massemail-type'
            inline
            value={type}
            onChange={onTypeChange}
            options={[
              { value: 'exam', label: 'Eksamen' },
              { value: 'athletics', label: 'Friidrett' },
            ]}
          />
        </div>

        <label className="ods-input__label ods-margin-top-3" style={{ display: 'block' }}>Emne
          <input className="ods-input__input" value={subject} onChange={e => setSubject(e.target.value)} placeholder='Emne på e-posten' />
        </label>

        <div className="ods-margin-top-3" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span className="ods-input__label" style={{ margin: 0 }}>E-posttekst (HTML)</span>
          {type && (
            <button type="button" className="ods-button ods-button--small ods-button--outline" onClick={insertTemplate}>
              Sett inn standardmal
            </button>
          )}
        </div>
        <textarea
          ref={bodyRef}
          className="ods-input__input"
          style={{ minHeight: '16rem', fontFamily: 'monospace', width: '100%' }}
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder={'Hei,\n\nHer er en oppdatering …\n\n{{registrering}}'}
        />
        <p className="ods-text--size-mike ods-color-text-grayscale-10" style={{ margin: '0.25rem 0 0' }}>
          Sendes som HTML. Variablene under fylles ut per mottaker.
        </p>

        <div className="ods-margin-top-2" style={{ border: '1px solid #e0e0e0', borderRadius: 4, padding: '0.75rem 1rem' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Variabler som kan settes inn</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {VARIABLES.map(v => (
              <button
                key={v.token}
                type="button"
                className="ods-button ods-button--small ods-button--outline"
                title={v.help}
                onClick={() => insertVariable(v.token)}
              >
                + {v.label}
              </button>
            ))}
          </div>
          <ul className="ods-text--size-mike ods-color-text-grayscale-10" style={{ margin: '0.6rem 0 0 1rem', padding: 0 }}>
            {VARIABLES.map(v => (
              <li key={v.token}><code>{v.token}</code> — {v.help}</li>
            ))}
          </ul>
        </div>

        {/* Recipients */}
        <div className="ods-margin-top-4">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <h3 style={{ margin: 0 }}>
              Mottakere {type && !loadingRecipients && <span className="ods-color-text-grayscale-10">({selected.size}/{recipients.length} valgt)</span>}
            </h3>
            {recipients.length > 0 && (
              <div className="ods-search-inline" style={{ maxWidth: '20rem' }}>
                <label className="ods-search-inline__label">
                  <input
                    placeholder='Søk på e-post eller navn'
                    className="ods-search-inline__input"
                    type="search"
                    autoComplete="off"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </label>
              </div>
            )}
          </div>

          {!type && <p className="ods-color-text-grayscale-10">Velg en påmeldingstype for å se mottakere.</p>}
          {loadingRecipients && <OKomLoading text='Henter mottakere …' />}

          {type && !loadingRecipients && (
            <>
              {recipients.length === 0 ? (
                <p className="ods-color-text-grayscale-10">Ingen mottakere funnet for denne påmeldingstypen.</p>
              ) : (
                <div style={{ border: '1px solid #e0e0e0', borderRadius: 4, maxHeight: '22rem', overflowY: 'auto' }}>
                  <label className="ods-checkbox" style={{ display: 'block', padding: '0.5rem 0.75rem', borderBottom: '1px solid #eee', background: '#fafafa', fontWeight: 600 }}>
                    <span className="ods-checkbox__wrapper">
                      <input type="checkbox" checked={allFilteredSelected} onChange={e => toggleAllFiltered(e.target.checked)} />
                      <span className="ods-checkbox__checkmark"></span>
                      <span className="ods-checkbox__text">Velg alle{search ? ' (treff)' : ''}</span>
                    </span>
                  </label>
                  {filtered.map(r => (
                    <label key={r.Email} className="ods-checkbox" style={{ display: 'block', padding: '0.4rem 0.75rem', borderBottom: '1px solid #f2f2f2' }}>
                      <span className="ods-checkbox__wrapper">
                        <input type="checkbox" checked={selected.has(r.Email)} onChange={e => toggleOne(r.Email, e.target.checked)} />
                        <span className="ods-checkbox__checkmark"></span>
                        <span className="ods-checkbox__text">
                          {r.Email}
                          {r.Names && <span className="ods-color-text-grayscale-10"> — {r.Names}</span>}
                        </span>
                      </span>
                    </label>
                  ))}
                  {filtered.length === 0 && (
                    <p className="ods-color-text-grayscale-10" style={{ padding: '0.75rem' }}>Ingen treff.</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="ods-margin-top-4 ods-margin-bottom-5" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button type="button" className="ods-button ods-button--outline" disabled={!canPreview} onClick={openPreview}>
            Forhåndsvis
          </button>
          <button type="button" className="ods-button" disabled={!canSend} onClick={() => setConfirmOpen(true)}>
            Send til {selected.size} mottaker{selected.size === 1 ? '' : 'e'}
          </button>
        </div>
      </div>

      <OKomModal open={previewOpen} onClose={() => setPreviewOpen(false)} labelledBy="massemail-preview-title" popupStyle={{ maxWidth: '52rem', width: '92vw' }}>
        <h2 id="massemail-preview-title" style={{ marginTop: 0 }}>Forhåndsvisning</h2>
        <p className="ods-text--size-mike ods-color-text-grayscale-10" style={{ marginTop: 0 }}>
          {previewRecipient
            ? <>Variablene er fylt ut for <strong>{previewRecipient}</strong>. Hver mottaker får sine egne data.</>
            : 'Ingen mottakerdata funnet – variablene vises tomme.'}
        </p>
        {previewLoading ? (
          <OKomLoading text='Lager forhåndsvisning …' />
        ) : (
          <iframe
            title="Forhåndsvisning av e-post"
            sandbox=""
            srcDoc={previewHtml}
            style={{ width: '100%', minHeight: '55vh', border: '1px solid #e0e0e0', borderRadius: 4, background: '#fff' }}
          />
        )}
        <div className="ods-margin-top-3" style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="ods-button ods-button--outline" onClick={() => setPreviewOpen(false)}>Lukk</button>
        </div>
      </OKomModal>

      <OKomModal open={confirmOpen} onClose={() => { if (!sending) setConfirmOpen(false); }} labelledBy="massemail-confirm-title" hideCloseButton popupStyle={{ maxWidth: '34rem' }}>
        <h2 id="massemail-confirm-title" style={{ marginTop: 0 }}>Bekreft utsending</h2>
        <p>
          Sende denne e-posten til <strong>{selected.size}</strong> mottaker{selected.size === 1 ? '' : 'e'}
          {type === 'exam' ? ' (eksamen)' : type === 'athletics' ? ' (friidrett)' : ''}? Variablene fylles ut per mottaker.
        </p>
        <div className="ods-margin-top-4" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button type="button" className="ods-button ods-button--outline" onClick={() => setConfirmOpen(false)} disabled={sending}>
            Avbryt
          </button>
          <button type="button" className="ods-button" onClick={send} disabled={sending}>
            Send nå
            {sending && <span className="ods-margin-left-1" style={{ verticalAlign: 'middle' }}><BeatLoader color="#ffffff" size={6} /></span>}
          </button>
        </div>
      </OKomModal>
    </>
  );
};

export default withRouter(MasseutsendelserView);
